import { useEffect, useState } from "react";
import { getBMPWalletAuditTrail } from "../../api/getBMPWalletAuditTrail";
import { BMP_WalletAuditTrail, WalletTransactionType } from "../../models/bmp.model";

interface AuditTrailTableProps {
  onBack?: () => void;
}

export default function AuditTrailTable({ onBack }: AuditTrailTableProps) {
  const [trail, setTrail] = useState<BMP_WalletAuditTrail[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getBMPWalletAuditTrail();
        setTrail(res);
      } catch (error) {
        console.error("Error fetching audit trail data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatAmount = (value: any, unit: string) => {
    const num = value !== null && value !== undefined ? Number(value) : 0;
    
    if (num === 0) {
      return (
        <div className="text-[11px] text-slate-300 font-mono tracking-tight my-0.5">
          0.00 <span className="text-[9px] opacity-60 font-sans">{unit}</span>
        </div>
      );
    }

    const isNegative = num < 0;
    const colorClass = isNegative ? "text-rose-600 font-semibold" : "text-emerald-600 font-semibold";
    const prefix = isNegative ? "" : "+";

    return (
      <div className={`text-[12px] font-mono tracking-tight my-0.5 ${colorClass}`}>
        {prefix}{num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} <span className="text-[9px] font-sans opacity-70 uppercase">{unit}</span>
      </div>
    );
  };

  const renderTypeTag = (type: WalletTransactionType) => {
    switch (type) {
      case WalletTransactionType.WITHDRAW:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-black tracking-wider bg-rose-50 text-rose-600 border border-rose-200 uppercase shadow-sm shadow-rose-100/50">
            <span className="w-1 h-1 rounded-full bg-rose-500" />
            Kifizetés
          </span>
        );
      case WalletTransactionType.DEPOSIT:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-black tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-200 uppercase shadow-sm shadow-emerald-100/50">
            <span className="w-1 h-1 rounded-full bg-emerald-500" />
            Befektetés
          </span>
        );
      case WalletTransactionType.TRANSFER:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-black tracking-wider bg-sky-50 text-sky-600 border border-sky-200 uppercase shadow-sm shadow-sky-100/50">
            <span className="w-1 h-1 rounded-full bg-sky-500" />
            Transzfer
          </span>
        );
      case WalletTransactionType.TRADE:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-black tracking-wider bg-amber-50 text-amber-600 border border-amber-200 uppercase shadow-sm shadow-amber-100/50">
            <span className="w-1 h-1 rounded-full bg-amber-500" />
            Kereskedés
          </span>
        );
      case WalletTransactionType.ROBOT: // Changed from Amber to Indigo for identity
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-black tracking-wider bg-violet-50 text-violet-600 border border-violet-200 uppercase shadow-sm shadow-violet-100/50">
            <span className="w-1 h-1 rounded-full bg-violet-500" />
            Robot termelése
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold bg-slate-50 text-slate-400 border border-slate-200 uppercase">
            —
          </span>
        );
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
      
      {/* Back Button */}
      <button 
        onClick={onBack} 
        className="group mb-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 font-medium transition-colors"
      >
        <span className="transition-transform group-hover:-translate-x-1">←</span> Vissza a főoldalra
      </button>

      {/* Header section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-5 border-b border-slate-100">
        <div>
          <h2 className="text-slate-800 font-extrabold tracking-tight text-xl sm:text-2xl">Audit Trail</h2>
          <p className="text-sm text-slate-400 mt-0.5">Tranzakciós napló és pénztárca változáskövetés</p>
        </div>
        <div className="self-start sm:self-center">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200 shadow-sm">
            {trail.length} rögzített esemény
          </span>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed min-w-[900px] border-collapse text-left">
            <thead>
              <tr className="bg-slate-50/70 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                <th className="w-[18%] p-4 pl-6">Dátum / Felelős</th>
                <th className="w-[12%] p-4">Típus</th>
                <th className="w-[16%] p-4 text-center bg-slate-50/30">Fix Tárca</th>
                <th className="w-[16%] p-4 text-center bg-purple-50/10">Robot Tárca</th>
                <th className="w-[18%] p-4 text-center bg-emerald-50/10">Trading Tárca</th>
                <th className="w-[20%] p-4 pr-6">Megjegyzés</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-600">
              {trail.length > 0 ? (
                trail.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/40 transition-colors group">
                    {/* Date / Actioned By */}
                    <td className="p-4 pl-6 vertical-align-top">
                      <div className="text-sm font-semibold text-slate-800">
                        {new Date(item.date).toLocaleDateString('hu-HU', { year:'numeric', month:'2-digit', day:'2-digit' })}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                         {item.createdBy?.name || "Rendszer"}
                      </div>
                    </td>

                    {/* Transaction Type Tag */}
                    <td className="p-4 align-middle">
                      {renderTypeTag(item.walletTransactionType)}
                    </td>

                    {/* FIX Wallet */}
                    <td className="p-4 text-center align-middle bg-slate-50/20 border-x border-slate-100">
                      <div className="inline-block text-left">
                        {formatAmount(item.fixBtc, "BTC")}
                        {formatAmount(item.fixEth, "ETH")}
                      </div>
                    </td>

                    {/* ROBOT Wallet */}
                    <td className="p-4 text-center align-middle bg-purple-50/10 border-x border-slate-100">
                      <div className="inline-block text-left">
                        {formatAmount(item.robotBtc, "BTC")}
                        {formatAmount(item.robotLiquidity, "USD")}
                      </div>
                    </td>

                    {/* TRADING Wallet */}
                    <td className="p-4 text-center align-middle bg-emerald-50/10 border-x border-slate-100">
                      <div className="inline-block text-left">
                        {formatAmount(item.tradingBtc, "BTC")}
                        {formatAmount(item.tradingEth, "ETH")}
                        {formatAmount(item.tradingLiquidity, "USD")}
                      </div>
                    </td>

                    {/* Note */}
                    <td className="p-4 pr-6 align-middle">
                      <div 
                        className="text-[12px] text-slate-500 leading-relaxed max-w-xs truncate" 
                        title={item.note || undefined}
                      >
                        {item.note || <span className="text-slate-300">—</span>}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="p-20 text-center text-slate-400 text-sm italic bg-slate-50/20">
                    {loading ? (
                      <div className="flex items-center justify-center gap-2 text-slate-500">
                        <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
                        Adatok betöltése...
                      </div>
                    ) : (
                      "Audit trail adatok jelenleg nem elérhetőek."
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}